import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import logoUrl from '../../assets/images/Senerfi-formato.png';
import { getQualityPrintMeta } from './qualityPrintConfig';

function esc(v) {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function fmtMoney0(n) {
  const v = Number(n || 0) || 0;
  try {
    return v.toLocaleString('es-CO', { maximumFractionDigits: 0, minimumFractionDigits: 0 });
  } catch (_) {
    const fixed = String(Math.round(v));
    return fixed.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }
}

function fmtDateDMY(value) {
  const s = String(value ?? '').trim();
  if (!s) return '';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

async function openExpenseRequestFrame(data) {
  const qualityMeta = await getQualityPrintMeta('expense_request');
  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.name = 'PRINT_SG';
  document.body.appendChild(frame);

  const w = frame.contentWindow;
  const doc = w.document;
  doc.open();

  const company = data.company || {};
  const req = data.request || {};
  const meta = data.meta || {};

  const basePath = (process.env.PUBLIC_URL || '/').replace(/\/+$/g, '');
  const abs = (p) => {
    const s = String(p || '').trim();
    if (!s) return '';
    if (/^data:image\//i.test(s)) return s;
    if (/^https?:\/\//i.test(s)) return s;
    if (s.startsWith('/')) return s;
    return `${basePath || ''}/${s}`.replace(/\/{2,}/g, '/');
  };
  const tryToDataUrl = async (src) => {
    const u = abs(src);
    if (!u) return '';
    if (/^data:image\//i.test(u)) return u;
    try {
      const resp = await fetch(u, { cache: 'no-store' });
      if (!resp.ok) return '';
      const blob = await resp.blob();
      if (blob && blob.type && !String(blob.type).toLowerCase().startsWith('image/')) return '';
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result || ''));
        reader.readAsDataURL(blob);
      });
    } catch (_) {
      return '';
    }
  };
  const candidates = [
    String(data.logoUrl || '').trim(),
    `${basePath || ''}${logoUrl}`,
    `${basePath || ''}/Senerfi-formato.png`,
  ].filter(Boolean);
  let logoSrc = '';
  for (const c of candidates) {
    const got = await tryToDataUrl(c);
    if (got) { logoSrc = got; break; }
  }

  const participantsRaw = Array.isArray(req.participants) ? req.participants : [];
  const participants = participantsRaw.length
    ? participantsRaw.map((p) => ({
      cedula: String(p?.cedula ?? p?.dni ?? '').trim(),
      nombre: String(p?.nombre ?? p?.name ?? '').trim(),
    })).filter((p) => p.cedula || p.nombre)
    : [{
      cedula: String(req.cedula ?? '').trim(),
      nombre: String(req.nombre ?? '').trim(),
    }];
  const lugar = String(req.lugar_actividad ?? req.lugarActividad ?? '').trim();
  const vehiculo = String(req.vehiculo ?? '').trim();
  const costCenter = String(req.cost_center ?? req.costCenter ?? '').trim();
  const proyecto = String(req.proyecto ?? '').trim();
  const actividad = String(req.actividad_realizar ?? req.actividadRealizar ?? '').trim();
  const fechaSolicitud = fmtDateDMY(req.created_at ?? req.createdAt ?? meta.created_at ?? '');
  const fechaInicio = fmtDateDMY(req.fecha_inicio ?? req.fechaInicio ?? '');
  const horaInicio = String(req.hora_inicio ?? req.horaInicio ?? '').trim();
  const fechaFin = fmtDateDMY(req.fecha_fin ?? req.fechaFin ?? '');

  const diasHotel = toNumber(req.dias_hotel ?? req.diasHotel ?? 0);
  const valorDiaHotel = toNumber(req.valor_dia_hotel ?? req.valorDiaHotel ?? 0);
  const valorComida = toNumber(req.valor_comida ?? req.valorComida ?? 0);
  const cantidadComidaRaw = toNumber(req.cantidad_comida ?? req.cantidadComida ?? 0);
  const cantidadComida = cantidadComidaRaw > 0 ? cantidadComidaRaw : (valorComida > 0 ? 1 : 0);
  const cantTransporte = toNumber(req.cantidad_transporte ?? req.cantidadTransporte ?? 0);
  const valorTransporte = toNumber(req.valor_transporte ?? req.valorTransporte ?? 0);
  const cantPeajes = toNumber(req.cantidad_peajes ?? req.cantidadPeajes ?? 0);
  const valorPeajes = toNumber(req.valor_peajes ?? req.valorPeajes ?? 0);

  const totalHotel = diasHotel * valorDiaHotel;
  const totalComida = cantidadComida * valorComida;
  const totalTransporte = cantTransporte * valorTransporte;
  const totalPeajes = cantPeajes * valorPeajes;
  const totalGasto = toNumber(req.total ?? (totalHotel + totalComida + totalTransporte + totalPeajes));

  const validationNotes = String(req.validation_notes ?? req.validationNotes ?? '').trim();
  const validatedByName = String(req.validated_by_name ?? req.validatedByName ?? '').trim();
  const validatedAt = fmtDateDMY(req.validated_at ?? req.validatedAt ?? '');
  const approvalNotes = String(req.approval_notes ?? req.approvalNotes ?? '').trim();
  const approvedByName = String(req.approved_by_name ?? req.approvedByName ?? '').trim();
  const approvedAt = fmtDateDMY(req.approved_at ?? req.approvedAt ?? '');
  const rejectNotes = String(req.reject_notes ?? req.rejectNotes ?? '').trim();
  const rejectedByName = String(req.rejected_by_name ?? req.rejectedByName ?? '').trim();
  const rejectedAt = fmtDateDMY(req.rejected_at ?? req.rejectedAt ?? '');
  const decisionBlocks = [
    (validationNotes || validatedByName) ? { label: 'Observación de validación', notes: validationNotes, by: validatedByName, at: validatedAt } : null,
    (approvalNotes || approvedByName) ? { label: 'Observación de aprobación', notes: approvalNotes, by: approvedByName, at: approvedAt } : null,
    (rejectNotes || rejectedByName) ? { label: 'Motivo de rechazo', notes: rejectNotes, by: rejectedByName, at: rejectedAt } : null,
  ].filter(Boolean);

  const code = String(req.code || meta.code || '').trim() || `SG-${String(req.id || '').trim()}`;
  const qualityCode = String(qualityMeta?.code || '').trim();
  const qualityVersion = String(qualityMeta?.version || '').trim();
  const qualityDate = String(qualityMeta?.effective_date_display || '').trim();

  const headerLogo = logoSrc
    ? `<img src="${esc(logoSrc)}" style="height:34px; object-fit:contain" />`
    : `<div style="font-weight:800;font-size:16px;letter-spacing:.4px">${esc(company.name || 'SENERFI S.A.S.')}</div>`;

  doc.write(`<!DOCTYPE html>
  <html>
  <head>
    <meta charset="utf-8" />
    <title>${esc(code)}</title>
    <style>
      * { box-sizing: border-box; }
      body { margin: 0; font-family: Arial, Helvetica, sans-serif; background: #fff; color: #111827; }
      .sheet { width: 1450px; padding: 10px 10px 14px 10px; }
      .hdr { border: 1px solid #111827; display: grid; grid-template-columns: 220px 1fr 220px; }
      .hdr > div { padding: 6px 8px; }
      .hdr-mid { text-align: center; border-left: 1px solid #111827; border-right: 1px solid #111827; }
      .hdr-mid .t1 { font-weight: 800; font-size: 12px; text-transform: uppercase; }
      .hdr-mid .t2 { font-weight: 800; font-size: 12px; text-transform: uppercase; margin-top: 2px; }
      .hdr-mid .t3 { font-weight: 800; font-size: 11px; text-transform: uppercase; margin-top: 2px; }
      .hdr-right { font-size: 11px; line-height: 1.35; }
      .hdr-right b { font-weight: 800; }
      .meta { margin-top: 6px; border: 1px solid #111827; border-top: 0; display: grid; grid-template-columns: 1fr; padding: 6px 8px; font-size: 11px; }
      .meta-row2 { margin-top: 3px; }
      table { width: 100%; table-layout: fixed; border-collapse: collapse; margin-top: 6px; }
      th, td { border: 1px solid #111827; padding: 6px 6px; font-size: 10px; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word; }
      th { background: #f2c5ad; text-transform: uppercase; font-weight: 800; text-align: center; }
      td { background: #fff; }
      .right { text-align: right; }
      .center { text-align: center; }
      .w-code { width: 90px; }
      .w-sm { width: 65px; }
      .w-xs { width: 55px; }
      .w-md { width: 110px; }
      .w-lg { width: 190px; }
      .totrow td { background: #f2c5ad; font-weight: 800; }
      .sig { margin-top: 14px; border-top: 1px solid #111827; padding-top: 8px; text-align: center; font-size: 11px; font-weight: 700; }
      .decisions { margin-top: 10px; }
      .decision-block { border: 1px solid #111827; margin-top: 6px; }
      .decision-label { background: #f2c5ad; font-weight: 800; font-size: 11px; text-transform: uppercase; padding: 5px 8px; }
      .decision-notes { padding: 8px; font-size: 11px; white-space: pre-wrap; min-height: 14px; }
      .decision-by { padding: 4px 8px 6px; font-size: 10px; color: #374151; border-top: 1px solid #d1d5db; }
      .muted { color: #374151; font-weight: 600; }
      .grand-total {
        margin-top: 8px;
        display: flex;
        justify-content: flex-end;
      }
      .grand-total-box {
        min-width: 280px;
        border: 1px solid #111827;
        background: #f2c5ad;
        padding: 8px 12px;
      }
      .grand-total-label {
        font-size: 11px;
        font-weight: 800;
        text-transform: uppercase;
      }
      .grand-total-value {
        margin-top: 4px;
        font-size: 18px;
        font-weight: 800;
        text-align: right;
      }
    </style>
  </head>
  <body>
    <div class="sheet">
      <div class="hdr">
        <div>${headerLogo}</div>
        <div class="hdr-mid">
          <div class="t1">${esc(company.subtitle || 'SOLUCIONES ENERGÉTICAS FORESTALES INTEGRALES S.A.S')}</div>
          <div class="t2">GESTIÓN COMPRAS</div>
          <div class="t3">SOLICITUD GASTOS DE VIAJE</div>
        </div>
        <div class="hdr-right">
          <div><b>CÓDIGO:</b> ${esc(qualityCode || '-')}</div>
          <div><b>VERSIÓN:</b> ${esc(qualityVersion || '-')}</div>
          <div><b>FECHA:</b> ${esc(qualityDate || '-')}</div>
        </div>
      </div>
      <div class="meta">
        <div><span class="muted">Solicitud:</span> ${esc(code)} ${fechaSolicitud ? ` | <span class="muted">Fecha solicitud:</span> ${esc(fechaSolicitud)}` : ''}</div>
        ${(costCenter || proyecto) ? `<div class="meta-row2"><span class="muted">Centro de costo:</span> ${esc(costCenter || '-')} | <span class="muted">Proyecto:</span> ${esc(proyecto || '-')}</div>` : ''}
      </div>

      <table>
        <thead>
          <tr>
            <th class="w-code">Fecha solicitud</th>
            <th class="w-sm">Cédula</th>
            <th class="w-md">Nombre</th>
            <th class="w-md">Lugar de actividad</th>
            <th class="w-sm">Vehículo</th>
            <th class="w-lg">Actividad a realizar</th>
            <th class="w-sm">Fecha inicio</th>
            <th class="w-xs">Hora inicio</th>
            <th class="w-sm">Fecha fin</th>
            <th class="w-xs">Días hotel</th>
            <th class="w-sm">Valor día de hotel</th>
            <th class="w-xs">Cantidad comida</th>
            <th class="w-sm">Valor x comida</th>
            <th class="w-sm">Cantidad transporte</th>
            <th class="w-sm">Valor transporte</th>
            <th class="w-sm">Cantidad peajes</th>
            <th class="w-sm">Valor peajes</th>
            <th class="w-sm">Total peajes</th>
            <th class="w-sm">Total gasto</th>
          </tr>
        </thead>
        <tbody>
          ${participants.map((person) => `
          <tr>
            <td class="center">${esc(fechaSolicitud || '-')}</td>
            <td class="center">${esc(person.cedula || '-')}</td>
            <td>${esc(person.nombre || '-')}</td>
            <td>${esc(lugar || '-')}</td>
            <td class="center">${esc(vehiculo || '-')}</td>
            <td>${esc(actividad || '-')}</td>
            <td class="center">${esc(fechaInicio || '-')}</td>
            <td class="center">${esc(horaInicio || '-')}</td>
            <td class="center">${esc(fechaFin || '-')}</td>
            <td class="right">${esc(diasHotel || 0)}</td>
            <td class="right">$ ${esc(fmtMoney0(valorDiaHotel))}</td>
            <td class="right">${esc(cantidadComida || 0)}</td>
            <td class="right">$ ${esc(fmtMoney0(valorComida))}</td>
            <td class="right">${esc(cantTransporte || 0)}</td>
            <td class="right">$ ${esc(fmtMoney0(valorTransporte))}</td>
            <td class="right">${esc(cantPeajes || 0)}</td>
            <td class="right">$ ${esc(fmtMoney0(valorPeajes))}</td>
            <td class="right">$ ${esc(fmtMoney0(totalPeajes))}</td>
            <td class="right">$ ${esc(fmtMoney0(totalGasto))}</td>
          </tr>`).join('')}
          <tr class="totrow">
            <td colspan="9" class="right">SUBTOTAL</td>
            <td class="right">${esc(diasHotel || 0)}</td>
            <td class="right">$ ${esc(fmtMoney0(totalHotel))}</td>
            <td class="right">${esc(cantidadComida || 0)}</td>
            <td class="right">$ ${esc(fmtMoney0(totalComida))}</td>
            <td class="right">${esc(cantTransporte || 0)}</td>
            <td class="right">$ ${esc(fmtMoney0(totalTransporte))}</td>
            <td class="right">${esc(cantPeajes || 0)}</td>
            <td class="right">$ ${esc(fmtMoney0(totalPeajes))}</td>
            <td class="right">$ ${esc(fmtMoney0(totalPeajes))}</td>
            <td class="right">$ ${esc(fmtMoney0(totalGasto))}</td>
          </tr>
        </tbody>
      </table>

      <div class="grand-total">
        <div class="grand-total-box">
          <div class="grand-total-label">Valor total de la solicitud</div>
          <div class="grand-total-value">$ ${esc(fmtMoney0(totalGasto))}</div>
        </div>
      </div>

      ${decisionBlocks.length ? `
      <div class="decisions">
        ${decisionBlocks.map((d) => `
        <div class="decision-block">
          <div class="decision-label">${esc(d.label)}</div>
          <div class="decision-notes">${esc(d.notes || '-')}</div>
          ${(d.by || d.at) ? `<div class="decision-by">${esc(d.by || '-')}${d.at ? ` · ${esc(d.at)}` : ''}</div>` : ''}
        </div>`).join('')}
      </div>` : ''}

      <div class="sig">COORDINADOR / SUPERVISOR SOLICITANTE</div>
    </div>
    <script>
      window.onload = () => { window.__SG_READY__ = true; };
    </script>
  </body>
  </html>`);
  doc.close();

  return { frame, w, title: code };
}

export async function generateExpenseRequestPdfBlob({ request, meta, company, logoUrl }) {
  const { frame, w, title } = await openExpenseRequestFrame({ request, meta, company, logoUrl });
  try {
    for (let i = 0; i < 60; i++) {
      if (w.__SG_READY__) break;
      await new Promise((r) => setTimeout(r, 50));
    }
    const imgs = Array.from(w.document.images || []);
    await Promise.all(imgs.map((img) => (
      img.complete ? Promise.resolve() : new Promise((resolve) => { img.onload = resolve; img.onerror = resolve; })
    )));
    await new Promise((r) => setTimeout(r, 60));

    const el = w.document.querySelector('.sheet') || w.document.body;
    const renderScale = 2;
    const canvas = await html2canvas(el, {
      scale: renderScale,
      backgroundColor: '#FFFFFF',
      useCORS: true,
      windowWidth: 1500,
      windowHeight: 900,
    });

    const pdf = new jsPDF({ orientation: 'l', unit: 'mm', format: 'a4' });
    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 6;
    const pxToMm = 0.26458333333333334;
    const mmW = (canvas.width / renderScale) * pxToMm;
    const mmH = (canvas.height / renderScale) * pxToMm;
    const maxW = pageW - (margin * 2);
    const pageInnerH = pageH - (margin * 2);
    const fitScale = Math.min(1, maxW / mmW);
    const drawW = mmW * fitScale;
    const drawH = mmH * fitScale;

    let offsetY = 0;
    pdf.addImage(imgData, 'JPEG', margin, margin - offsetY, drawW, drawH, undefined, 'FAST');
    while (offsetY + pageInnerH < drawH) {
      offsetY += pageInnerH;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', margin, margin - offsetY, drawW, drawH, undefined, 'FAST');
    }
    const blob = pdf.output('blob');
    return { blob, title };
  } finally {
    try { frame.remove(); } catch (_) {}
  }
}
